import { getProductName } from "@/utils/product-name"
import { useTranslation } from "react-i18next"
import VideoPlayer from "./VideoPlayer"
import { analytics } from "@/telemetry/amplitude"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { pixiesetAssetBaseURIs } from "@/utils/pixieset-urls"

type TutorialVideo = {
	title: string
	description: string
	youtubeId?: string
	vimeoUrl?: string
	hlsUrl?: string
	posterUrl?: string
}

function Tutorials() {
	const { t } = useTranslation("components")
	const isPixiesetAuth = isPixiesetAuthEnabled()
	const productName = getProductName()
	const legacyVideos: Record<string, TutorialVideo[]> = {
		[t("tutorials.sectionGetStarted")]: [
			{
				title: t("tutorials.cullingModeTitle"),
				description: t("tutorials.cullingModeDesc"),
				youtubeId: "o4IFFqTsmAc",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Use_Culling_Mode.m3u8`,
			},
			{
				title: t("tutorials.preTrainedStyleTitle"),
				description: t("tutorials.preTrainedStyleDesc"),
				youtubeId: "-FGjgN2cRyA",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Use_a_Polarr_Pre-Trained_AI_Style.m3u8`,
			},
			{
				title: t("tutorials.createFirstProjectTitle", { productName }),
				description: t("tutorials.createFirstProjectDesc"),
				youtubeId: "oQ782CBqSFI",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Create_Your_First_Project_and_AI_Style.m3u8`,
			},
			{
				title: t("tutorials.improveStyleTitle"),
				description: t("tutorials.improveStyleDesc"),
				youtubeId: "pi54UGprRdo",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Improve_Your_AI_Style_With_Reference_Edits.m3u8`,
			},
			{
				title: t("tutorials.transferLookTitle"),
				description: t("tutorials.transferLookDesc"),
				youtubeId: "_lq-dybfjUA",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Use_AI_Color_Match_in_Polarr_Next.m3u8`,
			},
			{
				title: t("tutorials.dataPersistenceTitle"),
				description: t("tutorials.dataPersistenceDesc", { productName }),
				youtubeId: "p7amUIfBtg4",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/Data_and_Persistence_FAQs.m3u8`,
			},
		],
		[t("tutorials.sectionEditing")]: [
			{
				title: t("tutorials.switchStylesTitle"),
				description: t("tutorials.switchStylesDesc"),
				youtubeId: "d6h-M41DH6E",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Use_a_Different_AI_Style_for_a_Current_Project.m3u8`,
			},
			{
				title: t("tutorials.lockEditTitle"),
				description: t("tutorials.lockEditDesc"),
				youtubeId: "GE3dgp-y3Fo",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_Does_Lock_Edit_Work.m3u8`,
			},
			{
				title: t("tutorials.reorderPhotosTitle"),
				description: t("tutorials.reorderPhotosDesc", { productName }),
				youtubeId: "pZB-YgrWCZw",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Reorder_Photos_in_a_Project.m3u8`,
			},
			{
				title: t("tutorials.virtualCopiesTitle"),
				description: t("tutorials.virtualCopiesDesc"),
				youtubeId: "ZnH8DM9s1Uk",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Create_a_Virtual_Copy.m3u8`,
			},
			{
				title: t("tutorials.hidePhotosTitle"),
				description: t("tutorials.hidePhotosDesc"),
				youtubeId: "J0TOWz1ACls",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Hide_Photos_in_a_Project.m3u8`,
			},
			{
				title: t("tutorials.locateLrPresetsTitle"),
				description: t("tutorials.locateLrPresetsDesc", { productName }),
				youtubeId: "iMQXXG6lIoQ",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Find_Your_LR_Presets_and_Profiles.m3u8`,
			},
			{
				title: t("tutorials.lrPresetAsBaseTitle"),
				description: t("tutorials.lrPresetAsBaseDesc"),
				youtubeId: "CQu3ezHJMdA",
				hlsUrl: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/How_to_Use_Your_LR_Preset_as_a_Base_Edit.m3u8`,
			},
		],
	}
	const tutorialVideos: Record<string, TutorialVideo[]> = {
		[t("tutorials.sectionGetStarted")]: [
			{
				title: t("tutorials.whatIsPhotoEditorTitle"),
				description: t("tutorials.whatIsPhotoEditorDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/ea/1/m3u8/4K%20Final%20V.6%20-%20Intro%20Photo%20Editor%20-%20Pixieset.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/ea/1/Thumbnail/V.2%20Final%20Thumbnail%20-%20Intro%20Video.jpg",
			},
			{
				title: t("tutorials.quickTourTitle"),
				description: t("tutorials.quickTourDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/ea/2/m3u8/4K%20Final%20-%20V.3%20-%20Pixieset%20Photo%20Editor%20Quick%20Tour.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/ea/2/thumbnail-20260616.jpg",
			},
			{
				title: t("tutorials.createProjectTitle"),
				description: t("tutorials.createProjectDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/dive-deeper/create-project/hls/index.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/dive-deeper/create-project/thumbnail.jpg",
			},
			{
				title: t("tutorials.adjustProjectNamesDatesTitle"),
				description: t("tutorials.adjustProjectNamesDatesDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/dive-deeper/adjust-project-names-dates/hls/index.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/dive-deeper/adjust-project-names-dates/thumbnail.jpg",
			},
		],
		Culling: [
			{
				title: t("tutorials.smartCullingTitle"),
				description: t("tutorials.smartCullingDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/ea/3/m3u8/4K%20Final%20v.4%20-%20Culling%20Tutorial%20-%20Pixieset%20Photo%20Editor.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/ea/3/thumbnail-20260616.jpg",
			},
		],
		[t("tutorials.sectionEditing")]: [
			{
				title: t("tutorials.smartEditingTitle"),
				description: t("tutorials.smartEditingDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/ea/4/m3u8/4K%20Final%20v.3%20-%20Editing%20Tutorial%20-%20Pixieset%20Photo%20Editor.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/ea/4/thumbnail-20260616.jpg",
			},
			{
				title: t("tutorials.retouchPhotosTitle"),
				description: t("tutorials.retouchPhotosDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/dive-deeper/ai-retouch-erase-gen-healing/hls-20260730/index.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/dive-deeper/ai-retouch-erase-gen-healing/thumbnail-20260616.jpg",
			},
			{
				title: t("tutorials.maskingToolsTitle"),
				description: t("tutorials.maskingToolsDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/dive-deeper/masking-tools/m3u8/index.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/dive-deeper/masking-tools/thumbnail-20260616.jpg",
			},
			{
				title: t("tutorials.virtualCopyGuideTitle"),
				description: t("tutorials.virtualCopyGuideDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/editing/virtual-copies/hls/index.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/editing/virtual-copies/thumbnail.jpg",
			},
			{
				title: t("tutorials.lockEditGuideTitle"),
				description: t("tutorials.lockEditGuideDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/editing/lock-edit/hls/index.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/editing/lock-edit/thumbnail.jpg",
			},
		],
		[t("tutorials.sectionAiStyles")]: [
			{
				title: t("tutorials.createAiStyleWithGalleryTitle"),
				description: t("tutorials.createAiStyleWithGalleryDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/ai-styles/create-ai-style-pixieset-gallery/hls/index.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/ai-styles/create-ai-style-pixieset-gallery/thumbnail-20260810.jpg",
			},
			{
				title: t("tutorials.createAiStyleByUploadTitle"),
				description: t("tutorials.createAiStyleByUploadDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/ai-styles/create-ai-style-upload-images/hls/index.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/ai-styles/create-ai-style-upload-images/thumbnail.jpg",
			},
			{
				title: t("tutorials.trainImproveAiStyleTitle"),
				description: t("tutorials.trainImproveAiStyleDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/dive-deeper/train-improve-ai-style/hls/index.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/dive-deeper/train-improve-ai-style/thumbnail-20260730.jpg",
			},
			{
				title: t("tutorials.createAiStyleBranchTitle"),
				description: t("tutorials.createAiStyleBranchDesc"),
				hlsUrl: "https://files-pe.pixieset.com/photoeditor/tutorials/ai-styles/create-ai-style-branch/hls/index.m3u8",
				posterUrl:
					"https://files-pe.pixieset.com/photoeditor/tutorials/ai-styles/create-ai-style-branch/thumbnail.jpg",
			},
		],
	}
	const videos = isPixiesetAuth ? tutorialVideos : legacyVideos

	const renderTutorials = () => {
		return (
			<div className="flex flex-col space-y-4">
				{Object.entries(videos).map(([sectionTitle, sectionVideos]) => (
					<div key={sectionTitle}>
						<div className="mb-5 text-md font-semibold text-base-400">
							{sectionTitle}
						</div>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
							{sectionVideos.map((video) => (
								<div
									key={video.vimeoUrl ?? video.youtubeId ?? video.title}
									className="mb-4 flex flex-col overflow-hidden rounded-lg bg-base-700"
								>
									<div style={{ paddingBottom: "56.25%", position: "relative" }}>
										<VideoPlayer
											title={video.title}
											vimeoUrl={video.vimeoUrl}
											youtubeId={video.youtubeId}
											hlsUrl={video.hlsUrl}
											posterUrl={video.posterUrl}
											onPlay={(target) => {
												analytics.trackEvent(
													{
														label: "play_tutorial",
														type: "click",
														view: video.title,
														customProperties: {
															origin: "tutorials",
														},
													},
													target,
												)
											}}
										/>
									</div>
									<div className="grow p-4">
										<div className="mb-1 text-md">{video.title}</div>
										<div className="text-sm text-base-400">
											{video.description}
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		)
	}

	return (
		<div className="flex w-full flex-col overflow-y-auto p-8">
			<div className="flex flex-row justify-between pb-8 text-lg">
				<div className="text-xl font-bold" aria-label={t("tutorials.ariaTutorials")}>
					{t("tutorials.tutorials")}
				</div>
			</div>
			{renderTutorials()}
		</div>
	)
}

export default Tutorials
